import { useEffect } from 'react';
import Header from '../../../components/base/Header';
import Footer from '../../../components/base/Footer';
import { Link } from 'react-router-dom';

export default function WinterCyclamenBlog() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-rose-50 to-orange-50">
      <Header />

      {/* Hero Section */}
      <section className="relative py-16 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block mb-6">
              <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                <i className="ri-snowy-line text-5xl text-white"></i>
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              【第1弾】季節の花の楽しみ方：冬編
            </h1>
            <p className="text-xl text-white/90 mb-6">
              ──シクラメンと過ごす、あたたかな冬──
            </p>
            <div className="flex items-center justify-center gap-4 text-white/90">
              <span className="flex items-center">
                <i className="ri-calendar-line mr-2"></i>
                2025年11月3日
              </span>
              <span className="flex items-center">
                <i className="ri-user-line mr-2"></i>
                ももちゃん
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Image */}
      <section className="py-8 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="rounded-3xl overflow-hidden shadow-2xl">
              <img
                src="https://static.readdy.ai/image/8ca6952e6a33257e04fb8ac67bcd75fe/eac34ebd96228ec8009c787c18aad611.jpeg"
                alt="シクラメン"
                className="w-full h-auto object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            {/* Introduction */}
            <div className="mb-12">
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                こんにちは、ももちゃんです。<br />
                今日は"冬の花の楽しみ方"として、<br />
                我が家でも毎年育てている <span className="font-bold text-purple-700">シクラメン</span> のお話をしたいと思います。
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                冬の花といえばポインセチアやパンジーも有名ですが、<br />
                私にとって冬といえば、やっぱりシクラメン。<br />
                あの丸い葉とふんわり反り返る花びらを見ると、<br />
                「あぁ、冬が来たんだな」とほっとした気持ちになります。
              </p>
            </div>

            {/* Section 1 */}
            <div className="mb-12 bg-gradient-to-br from-pink-50 to-rose-50 rounded-3xl p-8">
              <h2 className="text-3xl font-bold text-rose-800 mb-6 flex items-center">
                <span className="text-4xl mr-3">🌸</span>
                冬の花の特徴：シクラメンの"ぬくもり"
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  シクラメンは、冬にもっとも美しく咲く花。<br />
                  寒い季節にしっかり光を浴びて、<br />
                  室内で凛とした姿を見せてくれます。
                </p>
                <p>
                  ふっくらとした花弁、まあるい葉、<br />
                  そしてどこか人懐っこいシルエット。
                </p>
                <div className="bg-white rounded-2xl p-6 my-6 border-l-4 border-rose-400">
                  <p className="text-rose-700 font-medium text-lg">
                    冬の空気の中でふわりと咲く姿は、<br />
                    まるで "静かに寄り添ってくれる小さなランプ" のようです。
                  </p>
                </div>
                <p className="font-medium text-rose-800">
                  シクラメンが部屋にひとつあるだけで、<br />
                  空気がやわらかくなるような気がします。
                </p>
              </div>
            </div>

            {/* Section 2 */}
            <div className="mb-12 bg-gradient-to-br from-purple-50 to-indigo-50 rounded-3xl p-8">
              <h2 className="text-3xl font-bold text-purple-800 mb-6 flex items-center">
                <span className="text-4xl mr-3">🧣</span>
                我が家のシクラメン日記（体験談）
              </h2>
              <h3 className="text-xl font-bold text-purple-700 mb-6">
                ──毎年、親子で「お気に入り」を探しに
              </h3>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  わが家では、毎年の恒例行事があります。<br />
                  それは <span className="font-bold text-purple-700">シクラメン農家さんへ買いに行くこと</span>。
                </p>
                <p>
                  息子がシクラメンが大好きで、<br />
                  大きなビニールハウスの中を歩きながら<br />
                  「この子、元気！」「こっちは色がすごいね！」と<br />
                  宝探しのように楽しんでいます。
                </p>
                <p className="font-medium text-purple-800">
                  そして、うちには特に思い入れの深い子がいます。
                </p>
                <div className="grid md:grid-cols-3 gap-4 my-6">
                  <div className="bg-white rounded-2xl p-6 shadow-md text-center">
                    <div className="text-4xl mb-3">🌸</div>
                    <h3 className="text-lg font-bold text-purple-600 mb-2">3年間夏越し</h3>
                    <p className="text-sm text-gray-600">してくれたシクラメン</p>
                  </div>
                  <div className="bg-white rounded-2xl p-6 shadow-md text-center">
                    <div className="text-4xl mb-3">🌿</div>
                    <h3 className="text-lg font-bold text-purple-600 mb-2">原種のコウム</h3>
                    <p className="text-sm text-gray-600">小さくて可愛い</p>
                  </div>
                  <div className="bg-white rounded-2xl p-6 shadow-md text-center">
                    <div className="text-4xl mb-3">🍃</div>
                    <h3 className="text-lg font-bold text-purple-600 mb-2">アフリカナム</h3>
                    <p className="text-sm text-gray-600">葉模様が個性的</p>
                  </div>
                </div>
                <p>
                  これらは毎年冬になるたびに元気に復活してくれて、<br />
                  まるで家族の一員のような存在です。
                </p>
                <div className="bg-white rounded-2xl p-6 my-6">
                  <p className="text-gray-700 mb-4">
                    今年はすでに <span className="font-bold text-purple-700">2鉢購入</span>。<br />
                    大輪タイプと、農家さんが「比較的珍しいよ」と教えてくれた<br />
                    <span className="font-bold text-purple-700">イリュージア</span> という珍しい品種。
                  </p>
                  <p className="text-gray-700">
                    さらに、外用に <span className="font-bold text-purple-700">ガーデンシクラメン</span> も育てています。<br />
                    ただ、ガーデンの夏越しはなかなか難しい……。<br />
                    うちは <span className="font-bold text-purple-700">非休眠法</span> で頑張っていますが、<br />
                    正直、毎年試行錯誤です（笑）
                  </p>
                </div>
                <p className="font-medium text-purple-800 text-lg">
                  そんな経験も含めて、<br />
                  シクラメンは"育てる物語のある花"だと感じています。
                </p>
              </div>
            </div>

            {/* Section 3 */}
            <div className="mb-12 bg-gradient-to-br from-green-50 to-emerald-50 rounded-3xl p-8">
              <h2 className="text-3xl font-bold text-green-800 mb-6 flex items-center">
                <span className="text-4xl mr-3">🌱</span>
                初心者でも楽しめるシクラメンの3ポイント
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  シクラメンは難しそうに見えますが、<br />
                  コツを少し知れば、驚くほど長く楽しめます。
                </p>
                <div className="space-y-6 my-6">
                  <div className="bg-white rounded-2xl p-6 shadow-md">
                    <div className="flex items-start">
                      <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mr-4">
                        <span className="text-2xl font-bold text-green-700">①</span>
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-green-800 mb-2">
                          水は「葉の下から」そっと
                        </h3>
                        <p className="text-gray-700">
                          花や葉に直接かけると傷みやすいため、<br />
                          鉢の縁から静かにあげるのがコツ。
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white rounded-2xl p-6 shadow-md">
                    <div className="flex items-start">
                      <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mr-4">
                        <span className="text-2xl font-bold text-green-700">②</span>
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-green-800 mb-2">
                          寒すぎず、暑すぎず。15～20℃がご機嫌ゾーン
                        </h3>
                        <p className="text-gray-700">
                          暖房の風は苦手なので、<br />
                          風が当たらない明るい場所に置くと長持ちします。
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white rounded-2xl p-6 shadow-md">
                    <div className="flex items-start">
                      <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mr-4">
                        <span className="text-2xl font-bold text-green-700">③</span>
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-green-800 mb-2">
                          咲き終わった花は"くるっとひねって"取る
                        </h3>
                        <p className="text-gray-700">
                          花がら摘みをすると、<br />
                          次のつぼみが元気に育ってくれます。
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <p className="font-medium text-green-800 text-lg text-center">
                  これだけで初心者さんでも<br />
                  冬の間ずっとキレイに楽しめますよ。
                </p>
              </div>
            </div>

            {/* Section 4 */}
            <div className="mb-12 bg-gradient-to-br from-rose-50 to-pink-50 rounded-3xl p-8">
              <h2 className="text-3xl font-bold text-rose-800 mb-6 flex items-center">
                <span className="text-4xl mr-3">💐</span>
                冬におすすめの花言葉：シクラメン
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  シクラメンの花言葉は、<br />
                  <span className="font-bold text-rose-700">「内気」「はにかみ」「遠慮」</span>。
                </p>
                <p>
                  控えめだけれど、<br />
                  芯の強さと可愛らしさを持った花言葉です。
                </p>
                <p>
                  でも、冬にそっと寄り添って咲く姿を見ていると、<br />
                  私はもうひとつの意味を感じます。
                </p>
                <div className="bg-white rounded-2xl p-8 my-6 text-center border-2 border-rose-200">
                  <p className="text-2xl font-bold text-rose-800">
                    「あなたのペースで咲いていいよ」
                  </p>
                </div>
                <p className="font-medium text-rose-800">
                  そんな静かなメッセージが、<br />
                  この花にはある気がしています。
                </p>
              </div>
            </div>

            {/* Section 5 */}
            <div className="mb-12 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-3xl p-8">
              <h2 className="text-3xl font-bold text-blue-800 mb-6 flex items-center">
                <span className="text-4xl mr-3">❄️</span>
                おわりに──冬の時間をあたたかくする花
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  冬は寒くて、気持ちが内にこもりがちな季節。<br />
                  そんなときにシクラメンの小さな灯りは、<br />
                  心にふわっと明るさをくれる存在です。
                </p>
                <div className="bg-white rounded-2xl p-8 my-6 text-center border-2 border-blue-200">
                  <p className="text-xl font-bold text-blue-800 mb-4">
                    今年もまた、<br />
                    我が家の冬の花時間が始まります。
                  </p>
                  <p className="text-lg text-gray-700">
                    あなたもぜひ、<br />
                    お気に入りの一鉢と出会ってみてくださいね。
                  </p>
                </div>
              </div>
            </div>

            {/* Tags */}
            <div className="flex flex-wrap gap-2 mb-12">
              <span className="px-4 py-2 bg-rose-100 text-rose-700 text-sm rounded-full">
                #冬
              </span>
              <span className="px-4 py-2 bg-rose-100 text-rose-700 text-sm rounded-full">
                #シクラメン
              </span>
              <span className="px-4 py-2 bg-rose-100 text-rose-700 text-sm rounded-full">
                #季節の花
              </span>
              <span className="px-4 py-2 bg-rose-100 text-rose-700 text-sm rounded-full">
                #育て方
              </span>
              <span className="px-4 py-2 bg-rose-100 text-rose-700 text-sm rounded-full">
                #花言葉
              </span>
            </div>

            {/* Back Button */}
            <div className="text-center">
              <Link
                to="/essay"
                className="inline-flex items-center bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white font-bold py-4 px-8 rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl whitespace-nowrap"
              >
                <i className="ri-arrow-left-line mr-2"></i>
                エッセイ一覧に戻る
              </Link>
            </div>
          </div>
        </div>
      </article>

      {/* Related Posts */}
      <section className="py-16 bg-gradient-to-br from-pink-50 to-rose-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-rose-800 mb-8 text-center">
              他のブログ記事も読む
            </h2>
            <div className="text-center">
              <Link
                to="/essay"
                className="inline-flex items-center text-rose-600 hover:text-rose-700 font-medium transition-colors"
              >
                すべての記事を見る
                <i className="ri-arrow-right-line ml-2"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
